import { MouseEventHandler, PropsWithChildren, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Box } from 'reflexbox';
import styled from 'styled-components';

import { PageTitleSubLarge } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import Paragraph, { ParagraphSmall } from 'packages/kakapo-ui-kit/components/common/Paragraph';
import EditButton from 'packages/kakapo-ui-kit/components/editors/EditButton';
import DeleteButton from 'packages/kakapo-ui-kit/components/editors/DeleteButton';
import Button, { ButtonSize } from 'packages/kakapo-ui-kit/components/editors/Button';
import Spinner from 'packages/kakapo-ui-kit/components/common/Spinner';

export interface IProps {
	empty?: string;
	title?: string;
	description?: string;
	toolbar?: ReactNode;
	subheader?: ReactNode;
}

const Title = styled(Paragraph)`
	color: ${(props) => props.theme.colors.slateBlue};
	margin-right: 10px;
`;

const Description = styled(ParagraphSmall)`
	max-width: 500px;
	color: ${(props) => props.theme.colors.lightBlueDark};
`;

const SettingTitle = styled(PageTitleSubLarge)`
	line-height: 24px;
	color: ${(props) => props.theme.colors.slateBlue};
`;

export const SettingsEmptyState = styled(Paragraph)`
	display: flex;
	align-items: center;
	justify-content: center;
	color: ${(props) => props.theme.colors.lightBlueDark};
	text-align: center;
	height: 100px;
`;

export const SettingsRow = (
	props: PropsWithChildren<{
		px?: number;
		borderless?: boolean;
		loading?: boolean;
		valid?: boolean;
		onEdit?: MouseEventHandler<HTMLElement>;
		onRestore?: MouseEventHandler<HTMLElement>;
		onSave?: MouseEventHandler<HTMLElement>;
		onDelete?: MouseEventHandler<HTMLElement>;
	}>,
) => {
	const { t } = useTranslation();

	return (
		<>
			<Flex py="18px" px={props.px ?? 3} style={{ minHeight: 64 }} alignItems="center">
				<Flex flexDirection="row" alignSelf="center" flex={1}>
					<Box flexGrow={1}>
						{typeof props.children === 'string' ? (
							<SettingTitle>{props.children}</SettingTitle>
						) : (
							props.children
						)}
					</Box>
				</Flex>
				{(props.loading || props.onEdit || props.onRestore || props.onSave || props.onDelete) && (
					<Flex alignSelf="center" ml={4}>
						{props.loading ? (
							<Spinner isLoading size="18px" verticalMargin={0} />
						) : (
							<>
								{props.onRestore && (
									<Box ml="12px">
										<Button size={ButtonSize.Small} onClick={props.onRestore}>
											{t('common.restore')}
										</Button>
									</Box>
								)}

								{props.onSave && (
									<Box ml="12px">
										<Button
											size={ButtonSize.Small}
											onClick={props.onSave}
											enabled={props.valid !== false}
										>
											{t('common.save')}
										</Button>
									</Box>
								)}

								{props.onEdit && (
									<Box ml="12px">
										<EditButton onClick={props.onEdit} size={24} />
									</Box>
								)}

								{props.onDelete && (
									<Box ml="12px">
										<DeleteButton onClick={props.onDelete} size={24} />
									</Box>
								)}
							</>
						)}
					</Flex>
				)}
			</Flex>

			{props.borderless !== true && <HorizontalRule color="lightBlue" />}
		</>
	);
};

export default function SettingsTable({
	title,
	children,
	empty,
	description,
	toolbar,
	subheader,
}: PropsWithChildren<IProps>): ReactElement {
	return (
		<>
			{(title || description) && (
				<Flex pt={2} pb={3} alignItems="center">
					<Box flexGrow={1} flexShrink={1}>
						<Box pb={2}>
							<Title>{title}</Title>
						</Box>
						<Description>{description}</Description>
					</Box>
					{toolbar}
				</Flex>
			)}
			{subheader && <Box mb={3}>{subheader}</Box>}
			<Flex flexDirection="column">
				<HorizontalRule color="lightBlue" />
				{children}
			</Flex>
		</>
	);
}
